/**
 * Browser-only helpers for getting text out of the app. Both are guarded on `document`, so they
 * are safe to import from shared code that also ships to React Native (they just report false).
 */

export const canExportText = (): boolean => typeof document !== 'undefined';

/** Saves text as a file through a temporary <a download>. Works in all current browsers. */
export function downloadTextFile(fileName: string, text: string, mimeType = 'text/csv;charset=utf-8'): boolean {
  if (!canExportText()) {
    return false;
  }
  // A leading BOM makes Excel read the file as UTF-8.
  const blob = new Blob(['\uFEFF', text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 30000);
  return true;
}

/** Copies text to the clipboard: Clipboard API (HTTPS/localhost) with an execCommand fallback. */
export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (!canExportText()) {
    return false;
  }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to the legacy path
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
}
